import strings from 'utils/strings'
import {
  length,
  cond,
  always,
  pick,
  prop,
  has,
  T,
  propOr,
  uniq,
  propEq,
  isEmpty,
  head,
  equals,
} from 'ramda'
import { compareRpBaseNumber } from 'utils/referenceParameter'
import {
  EDITABLE_STATUSES,
  RESOURCE_TYPES,
  NOT_SUPPORTED,
  OBSOLETE as OBSOLETE_STRING,
  MASTER_RP_NUMBER_32AP1,
} from 'utils/constants'
import { isNumber, isInt } from 'utils/types'

export const UPDATE = 'UPDATE'
export const ALREADY_ON = 'ALREADY_ON'
export const ALREADY_STAGED = 'ALREADY_STAGED'
export const BASE_NUMBER_ALREADY_STAGED = 'BASE_NUMBER_ALREADY_STAGED'
export const INCOMPATIBLE_MASTER_32A = 'INCOMPATIBLE_MASTER_32A'
export const MAX_RESOURCES = 'MAX_RESOURCES'
export const MAX_TIMERS = 'MAX_TIMERS'
export const MAX_LOGICS = 'MAX_LOGICS'
export const NO_CONFLICTS = 'NO_CONFLICTS'
export const OBSOLETE = 'OBSOLETE'

export const ADD_RP_CONFLICT_MESSAGE_TRANSLATIONS = {
  [UPDATE]: strings.status_update_required,
  [ALREADY_ON]: strings.already_on_vehicle,
  [ALREADY_STAGED]: strings.already_staged,
  [BASE_NUMBER_ALREADY_STAGED]: strings.base_number_already_staged,
  [INCOMPATIBLE_MASTER_32A]: strings.not_supported_by_chec,
  [MAX_RESOURCES]: strings.max_allowed_resources,
  [MAX_TIMERS]: strings.max_allowed_timers_restriction,
  [MAX_LOGICS]: strings.max_allowed_logics_restriction,
  [NO_CONFLICTS]: strings.no_conflicts,
  [NOT_SUPPORTED]: strings.not_compatible_with_the_vehicle,
  [OBSOLETE]: strings.rp_is_obsolete,
}

// Conflict messages that indicate true vehicle incompatibility.
// When "Only Show Compatible RPs" filter is active, RPs with these
// conflict messages should be hidden from results (not just disabled).
const FILTERABLE_CONFLICT_MESSAGES = new Set([
  ADD_RP_CONFLICT_MESSAGE_TRANSLATIONS[INCOMPATIBLE_MASTER_32A],
  ADD_RP_CONFLICT_MESSAGE_TRANSLATIONS[NOT_SUPPORTED],
  ADD_RP_CONFLICT_MESSAGE_TRANSLATIONS[OBSOLETE],
])

export const isFilterableConflict = (conflictMessage) =>
  Boolean(conflictMessage) && FILTERABLE_CONFLICT_MESSAGES.has(conflictMessage)

export const hasConflictMessage = (
  rpToBeAdded = {}, // the table record
  referenceParameters = [], // rps on vehicle
  stagedRps = [], // rps staged from add workflow
  timerCount = 0, // avaliable timer slots
  logicCount = 0, // avaliable logic slots
  maxTimerCount,
  maxLogicCount
) => {
  // basic conflict checks
  const basicConflictMessage = checkBasicConflicts(
    rpToBeAdded,
    referenceParameters,
    stagedRps,
    maxTimerCount,
    maxLogicCount
  )

  if (basicConflictMessage)
    return ADD_RP_CONFLICT_MESSAGE_TRANSLATIONS[basicConflictMessage]

  // resource conflicts check
  const resourceConflictMessage = checkResourceConflicts(
    rpToBeAdded,
    referenceParameters,
    stagedRps,
    timerCount,
    logicCount
  )

  return ADD_RP_CONFLICT_MESSAGE_TRANSLATIONS[resourceConflictMessage]
}

export const isInEditableStatus = ({ status }) =>
  EDITABLE_STATUSES.includes(status)

export const isAlreadyOnVehicle = ({ uri, alreadyOn }) =>
  alreadyOn.some((rp) => prop('uri', rp) === uri)

export const isBaseNumberAlreadyStaged = ({
  refParameterNumber,
  release,
  alreadyStaged,
}) =>
  alreadyStaged.some((rp) =>
    compareRpBaseNumber(rp, { refParameterNumber, release })
  )

export const isAlreadyStaged = ({ refParameterNumber, alreadyStaged }) =>
  alreadyStaged.some(propEq('refParameterNumber', refParameterNumber))

export const isMaxResources = ({
  adjustedTimersAvailable,
  adjustedLogicsAvailable,
}) => adjustedTimersAvailable < 0 && adjustedLogicsAvailable < 0

export const isMaxTimers = ({ adjustedTimersAvailable }) =>
  isNumber(adjustedTimersAvailable) && adjustedTimersAvailable < 0

export const isMaxLogics = ({ adjustedLogicsAvailable }) =>
  isNumber(adjustedLogicsAvailable) && adjustedLogicsAvailable < 0

const isObsolete = ({ status }) => equals(OBSOLETE_STRING, status)
const isNotSupported = ({ status }) => equals(NOT_SUPPORTED, status)
export const isIncompatibleMaster32A = ({ masterNumber }) =>
  Boolean(masterNumber && masterNumber.includes(MASTER_RP_NUMBER_32AP1))

export const basicConflicts = cond([
  [isInEditableStatus, always(UPDATE)],
  [isAlreadyOnVehicle, always(ALREADY_ON)],
  [isAlreadyStaged, always(ALREADY_STAGED)],
  [isBaseNumberAlreadyStaged, always(BASE_NUMBER_ALREADY_STAGED)],
  [isIncompatibleMaster32A, always(INCOMPATIBLE_MASTER_32A)],
  [isObsolete, always(OBSOLETE)],
  [isNotSupported, always(NOT_SUPPORTED)],
  [T, always(false)],
])

export const getResourceConflictType = cond([
  [isMaxResources, always(MAX_RESOURCES)],
  [isMaxTimers, always(MAX_TIMERS)],
  [isMaxLogics, always(MAX_LOGICS)],
  [T, always(NO_CONFLICTS)],
])

export const getResourceIdsFromRp = (type, rp) =>
  propOr([], type, rp).map(prop('id'))

export const getResourceIdsFromRps = (type, rps) =>
  rps.reduce(
    (acc, curr) =>
      has(type, curr) ? [...acc, ...getResourceIdsFromRp(type, curr)] : acc,
    []
  )

export const filterForNewResourceIds = (newIds, currentIds) =>
  newIds.filter((id) => !currentIds.includes(id))

// this is needed incase one resource can be added, but the other resource is over max already
export const shouldRequireAdditionalValidation = (
  message,
  alreadyAboveMaxTimers,
  alreadyAboveMaxLogics
) =>
  [MAX_TIMERS, MAX_LOGICS, MAX_RESOURCES].includes(message) &&
  (alreadyAboveMaxTimers || alreadyAboveMaxLogics)

export const rpIsAlreadyOnVehicle = (vehicleRPs, rpUri) =>
  vehicleRPs.some(({ uri }) => uri === rpUri)

// If less than 0 resources available, then already above max allowed resources
export const isAlreadyAboveMax = (resourcesAvailable) => resourcesAvailable < 0

export const checkBasicConflicts = (
  rpToBeAdded,
  referenceParameters,
  stagedRps,
  maxTimerCount,
  maxLogicCount
) => {
  // pre check totals on new rp don't exceed max for vehicle
  const exceedsTimers = propOr([], 'timers', rpToBeAdded).length > maxTimerCount
  const exceedsLogics = propOr([], 'logics', rpToBeAdded).length > maxLogicCount

  if (exceedsLogics && exceedsTimers) return MAX_RESOURCES

  if (exceedsTimers) return MAX_TIMERS

  if (exceedsLogics) return MAX_LOGICS

  const alreadyOn = referenceParameters.map(
    pick(['uri', 'refParameterNumber', 'release'])
  )
  const alreadyStaged = stagedRps.map(
    pick(['uri', 'refParameterNumber', 'release'])
  )

  // check for a basic conflict, bail early with conflict message
  const basicCheckValues = {
    status: rpToBeAdded.status,
    uri: rpToBeAdded.uri,
    refParameterNumber: rpToBeAdded.refParameterNumber,
    release: rpToBeAdded.release,
    masterNumber: rpToBeAdded.masterNumber,
    alreadyOn,
    alreadyStaged,
  }

  const conflictMessage = basicConflicts(basicCheckValues)

  return conflictMessage
}

export const shouldOverRideConflictMessage = ({
  resourceConflictMessage,
  timerCount,
  logicCount,
  adjustedTimersAvailable,
  adjustedLogicsAvailable,
}) => {
  // possible resource already above max value conflict check
  const alreadyAboveMaxTimers = isAlreadyAboveMax(timerCount)
  const alreadyAboveMaxLogics = isAlreadyAboveMax(logicCount)

  const willBeFurtherOverMaxTimers = alreadyAboveMaxTimers
    ? adjustedTimersAvailable < timerCount
    : false
  const willBeFurtherOverMaxLogics = alreadyAboveMaxLogics
    ? adjustedLogicsAvailable < logicCount
    : false

  const needsAdditionalValidation = shouldRequireAdditionalValidation(
    resourceConflictMessage,
    alreadyAboveMaxTimers,
    alreadyAboveMaxLogics
  )

  // handle case where one resource is already above a max, but adding new RP won't increase that resource count
  if (
    needsAdditionalValidation &&
    alreadyAboveMaxTimers &&
    !willBeFurtherOverMaxTimers &&
    adjustedLogicsAvailable >= 0
  )
    return true
  if (
    needsAdditionalValidation &&
    alreadyAboveMaxLogics &&
    !willBeFurtherOverMaxLogics &&
    adjustedTimersAvailable >= 0
  )
    return true

  return false
}

export const adjustAvailableResourceCount = (
  currentCount,
  newResource,
  currentResource
) =>
  currentCount - length(filterForNewResourceIds(newResource, currentResource))

export const adjustResourceCount = (openSlots, toBeAdded, toBeRemoved) =>
  openSlots - (toBeAdded - toBeRemoved)

export const isAddingResourceBlocked = (count, adjusted) => {
  if (count < 0 && adjusted < count) return true
  if (count < 0 && adjusted > count) return false // still be above max, but less above then started at
  if (adjusted < 0) return true
  return false
}

export const checkResourceConflicts = (
  rpToBeAdded,
  referenceParameters,
  stagedRps,
  timerCount,
  logicCount
) => {
  if (!isInt(timerCount) || !isInt(logicCount))
    throw new Error('timer and logic counts must be ints')

  // start resource conflict checks
  const rpsLessReplacingRps = referenceParameters.filter(
    (rp) => !willReplaceRP(rp, stagedRps)
  )
  const rpsBeingReplaced = referenceParameters.filter((rp) =>
    willReplaceRP(rp, stagedRps)
  )

  const currentTimers = uniq(
    getResourceIdsFromRps(RESOURCE_TYPES.TIMERS, rpsLessReplacingRps)
  )
  const currentLogics = uniq(
    getResourceIdsFromRps(RESOURCE_TYPES.LOGICS, rpsLessReplacingRps)
  )

  const rpToBeAddedTimers = getResourceIdsFromRp(
    RESOURCE_TYPES.TIMERS,
    rpToBeAdded
  )
  const rpToBeAddedLogics = getResourceIdsFromRp(
    RESOURCE_TYPES.LOGICS,
    rpToBeAdded
  )

  // eary escape if both timers and logics to be added is 0
  if (isEmpty(rpToBeAddedLogics) && isEmpty(rpToBeAddedTimers)) {
    return NO_CONFLICTS
  }

  // is replacing an RP, check if it will lower resource counts
  const rpToReplace = willReplaceRP(rpToBeAdded, referenceParameters)

  // will adding the new RP reduce the available resource count
  // can only increase count when replacing an RP handled below
  const rpToReplaceTimers = propOr([], 'timers', rpToReplace).map(
    ({ id }) => id
  )
  const currentTimersLessReplaced = currentTimers.filter(
    (timer) => !rpToReplaceTimers.includes(timer)
  )
  const newTimersToBeAdded = filterForNewResourceIds(
    [
      ...rpToBeAddedTimers,
      ...getResourceIdsFromRps(RESOURCE_TYPES.TIMERS, stagedRps),
    ],
    currentTimersLessReplaced
  )
  const timersToBeRemoved = filterForNewResourceIds(
    [
      ...propOr([], 'timers', rpToReplace),
      ...getResourceIdsFromRps(RESOURCE_TYPES.TIMERS, rpsBeingReplaced),
    ],
    currentTimers
  )

  const adjustedTimersAvailable = adjustResourceCount(
    timerCount,
    length(newTimersToBeAdded),
    length(timersToBeRemoved)
  )

  const rpToReplaceLogics = propOr([], 'logics', rpToReplace).map(
    ({ id }) => id
  )
  const currentLogicsLessReplaced = currentLogics.filter(
    (logic) => !rpToReplaceLogics.includes(logic)
  )
  const newLogicsToBeAdded = filterForNewResourceIds(
    [
      ...rpToBeAddedLogics,
      ...getResourceIdsFromRps(RESOURCE_TYPES.LOGICS, stagedRps),
    ],
    currentLogicsLessReplaced
  )

  const logicsToBeRemoved = filterForNewResourceIds(
    [
      ...propOr([], 'logics', rpToReplace),
      ...getResourceIdsFromRps(RESOURCE_TYPES.LOGICS, rpsBeingReplaced),
    ],
    currentLogics
  )

  const adjustedLogicsAvailable = adjustResourceCount(
    logicCount,
    length(newLogicsToBeAdded),
    length(logicsToBeRemoved)
  )

  if (rpToReplace) {
    const conflicts = []

    if (isAddingResourceBlocked(timerCount, adjustedTimersAvailable)) {
      conflicts.push(MAX_TIMERS)
    }

    if (isAddingResourceBlocked(logicCount, adjustedLogicsAvailable)) {
      conflicts.push(MAX_LOGICS)
    }

    if (length(conflicts) === 2) return MAX_RESOURCES

    if (length(conflicts) === 1) return head(conflicts)

    return NO_CONFLICTS
  }

  // Check for resource count conflicts
  const resourceCheckValues = {
    adjustedTimersAvailable,
    adjustedLogicsAvailable,
  }

  // basic resource conflict check
  const resourceConflictMessage = getResourceConflictType(resourceCheckValues)

  if (resourceConflictMessage !== NO_CONFLICTS) {
    // if a resource is already over the max, and is not being added to, it will throw a false conflict, need to override that
    const hasOverRide = shouldOverRideConflictMessage({
      resourceConflictMessage,
      timerCount,
      logicCount,
      adjustedTimersAvailable,
      adjustedLogicsAvailable,
    })

    if (hasOverRide) return NO_CONFLICTS
  }

  return resourceConflictMessage
}

export const willReplaceRP = (record, referenceParameters) =>
  referenceParameters.find((rp) => compareRpBaseNumber(rp, record))

export const countResourceInStagedRps = (type, rpsOnTruck, stagedRps) => {
  const rpsOnTruckLessReplacingRps = rpsOnTruck.filter(
    (rp) => !willReplaceRP(rp, stagedRps)
  )
  const replacingRps = rpsOnTruck.filter((rp) => willReplaceRP(rp, stagedRps))
  const replacingRpsResourcesIds = replacingRps
    .reduce((acc, curr) => {
      const resources = propOr([], type, curr)
      return [...acc, ...resources]
    }, [])
    .map(prop('id'))

  const rpsOnTruckAndStagedLessReplacingIds = stagedRps
    .concat(rpsOnTruckLessReplacingRps)
    .reduce((acc, curr) => {
      const resources = propOr([], type, curr)
      return [...acc, ...resources]
    }, [])
    .map(prop('id'))

  const uniqueResourcesRemoved = replacingRpsResourcesIds.filter(
    (id) => !rpsOnTruckAndStagedLessReplacingIds.includes(id)
  ).length

  const count = stagedRps.reduce(
    (acc, curr) => acc + resourceCount(type, rpsOnTruck, curr),
    0
  )

  return count - uniqueResourcesRemoved
}

const resourceCount = (type, rps, record) => {
  if (!record) return null

  const recordResources = record[type]

  if (!recordResources || recordResources.length === 0) {
    return 0
  }

  const currentResources = new Set(
    rps.reduce((accum, rp) => {
      const resources = rp[type]
      if (!resources || resources.length === 0) {
        return accum
      }
      return [...accum, ...resources.map(({ id }) => id)]
    }, [])
  )

  const newResources = recordResources.filter(
    ({ id }) => !currentResources.has(id)
  )

  const newResourcesCount = length(newResources)

  return newResourcesCount
}
